// src/components/layout/PageHeader.jsx
import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../../hooks/useAppDispatch.js'
import { motion, AnimatePresence } from 'framer-motion'
import { gsap } from 'gsap'
import { IoClose } from 'react-icons/io5'
import { IoMenuOutline } from 'react-icons/io5'
import { IoIosArrowDown } from 'react-icons/io'
import { IoArrowForward } from "react-icons/io5";
import { FaArrowRight } from "react-icons/fa";
import { FaBriefcase } from "react-icons/fa";
import { FaRegQuestionCircle } from "react-icons/fa";
import { FaMapMarkerAlt } from "react-icons/fa";
import { openModal } from '../../redux/slices/modalSlice.js';
import { fetchBlogs } from '../../redux/slices/blogsSlice.js'; // Import fetchBlogs
import { formatDate } from '../../utils/formatDate.js'; // Import formatDate utility
import { LazyImage } from '../common/LazyImage.jsx'; // Import LazyImage
import StarBorder from '../common/Animated/StarBorder.jsx'

import './PageHeader.scss'

export default function PageHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(null)
  const [activeItem, setActiveItem] = useState(null)
  const [isHoveringDropdown, setIsHoveringDropdown] = useState(false)
  const [hasScrolledPast100, setHasScrolledPast100] = useState(false)
  const [showNavbar, setShowNavbar] = useState(true)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1100)
  const [insightsFetched, setInsightsFetched] = useState(false)

  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // Get data from redux slices
  const { nav, loading: globalLoading } = useAppSelector((state) => state.global)
  const { blogs, loading: blogsLoading } = useAppSelector((state) => state.blogs)

  const handleOpenContactModal = () => {
    dispatch(openModal({
      modalType: 'contact',
      modalProps: {}
    }));
  };

  const underlineRef = useRef()
  const itemsRef = useRef([])
  const navCenterRef = useRef()
  const dropdownRef = useRef()
  const hoverTimeoutRef = useRef(null)
  const lastScrollY = useRef(0)

  // Fetch latest insights when needed
  useEffect(() => {
    if (activeItem !== null && nav?.navItems?.[activeItem]?.key === 'company' && !insightsFetched) {
      dispatch(fetchBlogs({ page: 1 }))
      setInsightsFetched(true)
    }
  }, [activeItem, nav, dispatch, insightsFetched])

  // Create dropdown content dynamically from global slice
  const createDropdownContent = () => {
    const companyLinks = nav?.navItems?.find(item => item.key === 'company')?.links || []
    const insightsLinks = nav?.navItems?.find(item => item.key === 'insights')?.links || []

    // Get latest insights from blogs slice
    const latestInsights = blogs?.blogs?.slice(0, 2).map(blog => ({
      image: blog.hero?.imageUrl || "https://via.placeholder.com/150x85",
      title: blog.title || 'Untitled Blog',
      tech: blog.tags?.slice(0, 2) || blog.categories?.slice(0, 2) || ['Blog', 'Insights'],
      link: `/blog/${blog.slug}`,
      id: blog._id,
      publishedAt: blog.publishedAt || blog.createdAt
    })) || [
        {
          image: "https://via.placeholder.com/150x85",
          title: "No insights available",
          tech: ["Coming Soon"],
          link: "/blogs",
          id: 0
        }
      ]

    // Get modal data from global config for contact info
    const modalData = nav?.modal || {}
    const testimonialsData = nav?.testimonials || {}

    // Company dropdown items from global config
    const companyItems = companyLinks.map((link, index) => {
      let icon
      switch (link.label.toLowerCase()) {
        case 'about':
          icon = <FaRegQuestionCircle style={{ color: "var(--primary-deeper)" }} fontSize={14} />
          break
        case 'careers':
          icon = <FaBriefcase style={{ color: "var(--primary-deeper)" }} fontSize={14} />
          break
        case 'contact':
          icon = <FaMapMarkerAlt style={{ color: "var(--primary-deeper)" }} fontSize={14} />
          break
        default:
          icon = <FaArrowRight style={{ color: "var(--primary-deeper)" }} fontSize={14} />
      }

      return {
        icon,
        title: link.label,
        desc: getCompanyItemDescription(link.label),
        link: link.path,
        id: index
      }
    })

    // Services dropdown items
    const servicesItems = (nav?.navItems?.find(item => item.key === 'services')?.links || [])
      .slice(0, 8)
      .map((link, index) => ({
        icon: <FaArrowRight style={{ color: "var(--primary-deeper)" }} fontSize={12} />,
        title: link.label,
        desc: getServiceDescription(link.label),
        link: link.path,
        id: index
      }))

    // Industries dropdown items
    const industriesItems = (nav?.navItems?.find(item => item.key === 'industries')?.links || [])
      .slice(0, 8)
      .map((link, index) => ({
        icon: <FaArrowRight style={{ color: "var(--primary-deeper)" }} fontSize={12} />,
        title: link.label,
        desc: getIndustryDescription(link.label),
        link: link.path,
        id: index
      }))

    // Insights dropdown items
    const insightsItems = insightsLinks.map((link, index) => ({
      icon: <FaArrowRight style={{ color: "var(--primary-deeper)" }} fontSize={12} />,
      title: link.label,
      desc: getInsightsDescription(link.label),
      link: link.path,
      id: index
    }))

    return {
      services: {
        cols: [
          servicesItems.slice(0, Math.ceil(servicesItems.length / 2)),
          servicesItems.slice(Math.ceil(servicesItems.length / 2))
        ].filter(col => col.length > 0)
      },
      industries: {
        cols: [
          industriesItems.slice(0, Math.ceil(industriesItems.length / 2)),
          industriesItems.slice(Math.ceil(industriesItems.length / 2))
        ].filter(col => col.length > 0)
      },
      insights: {
        cols: insightsItems.length > 0
          ? [
            insightsItems.slice(0, Math.ceil(insightsItems.length / 2)),
            insightsItems.slice(Math.ceil(insightsItems.length / 2))
          ].filter(col => col.length > 0)
          : [[
            { icon: <FaArrowRight style={{ color: "var(--primary-deeper)" }} fontSize={12} />, title: "Blogs", desc: "Latest articles and insights", link: "/blogs", id: 0 },
            { icon: <FaArrowRight style={{ color: "var(--primary-deeper)" }} fontSize={12} />, title: "Case Studies", desc: "Success stories and projects", link: "/case-studies", id: 1 }
          ]]
      },
      company: {
        type: "special",
        leftCol: companyItems,
        rightTitle: testimonialsData.heading || "Latest Insights",
        insights: latestInsights,
        modalData: modalData // Pass modal data for contact info
      }
    }
  }

  // Helper functions for descriptions
  const getCompanyItemDescription = (label) => {
    const lower = label.toLowerCase()
    if (lower.includes('about')) return "Learn about our mission and values"
    if (lower.includes('career')) return "Join our talented team"
    if (lower.includes('contact')) return "Get in touch with our team"
    return "Learn more about our company"
  }

  const getServiceDescription = (label) => {
    return `Professional ${label.toLowerCase()} services`
  }

  const getIndustryDescription = (label) => {
    return `${label.toLowerCase()} industry solutions`
  }

  const getInsightsDescription = (label) => {
    const lower = label.toLowerCase()
    if (lower.includes('blog')) return "Latest articles and insights"
    if (lower.includes('webinar')) return "Upcoming events and webinars"
    if (lower.includes('case')) return "Success stories and projects"
    return "Latest updates and resources"
  }

  const dropdownContent = createDropdownContent()

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1100
      setIsMobile(mobile)

      if (mobile) {
        setActiveItem(null)
      } else {
        setIsMenuOpen(false)
        setMobileDropdownOpen(null)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Handle hover outside dropdown
  useEffect(() => {
    const handleHoverOutside = (event) => {
      if (
        !isMobile &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        navCenterRef.current &&
        !navCenterRef.current.contains(event.target) &&
        !isHoveringDropdown
      ) {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current)
        }

        hoverTimeoutRef.current = setTimeout(() => {
          setActiveItem(null)
        }, 100)
      }
    }

    document.addEventListener('mousemove', handleHoverOutside)
    return () => {
      document.removeEventListener('mousemove', handleHoverOutside)
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [isMobile, isHoveringDropdown])

  // Animate underline
  useEffect(() => {
    if (activeItem !== null && !isMobile) {
      const activeElement = itemsRef.current[activeItem]
      if (activeElement && underlineRef.current) {
        gsap.to(underlineRef.current, {
          left: activeElement.offsetLeft,
          width: activeElement.offsetWidth,
          duration: 0.4,
          ease: "power3.out",
        })
      }
    }
  }, [activeItem, isMobile])

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      if (currentScrollY > 100 && !hasScrolledPast100) {
        setHasScrolledPast100(true)
      } else if (currentScrollY <= 100 && hasScrolledPast100) {
        setHasScrolledPast100(false)
        setShowNavbar(true)
      }

      if (hasScrolledPast100) {
        if (currentScrollY > lastScrollY.current) {
          if (showNavbar) {
            setShowNavbar(false)
          }
        } else {
          if (lastScrollY.current - currentScrollY > 15 && !showNavbar) {
            setShowNavbar(true)
          }
        }
      }

      lastScrollY.current = currentScrollY
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [hasScrolledPast100, showNavbar])

  // Desktop navigation handlers
  const handleItemHover = (index) => {
    if (isMobile) return

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }

    setActiveItem(index)
  }

  const handleItemClick = (index) => {
    if (isMobile) return

    const item = nav?.navItems?.[index]
    if (!item) return

    const basePath = getBasePathForKey(item.key)
    navigate(basePath)

    setActiveItem(null)
  }

  const handleDropdownHover = () => {
    setIsHoveringDropdown(true)
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
  }

  const handleDropdownLeave = () => {
    setIsHoveringDropdown(false)
    hoverTimeoutRef.current = setTimeout(() => {
      setActiveItem(null)
    }, 1000)
  }

  // Mobile navigation handlers
  const handleMobileItemClick = (itemKey) => {
    setMobileDropdownOpen(mobileDropdownOpen === itemKey ? null : itemKey)
  }

  const handleMobileLinkClick = (link) => {
    navigate(link)
    setIsMenuOpen(false)
    setMobileDropdownOpen(null)
  }

  const handleDesktopLinkClick = (link) => {
    navigate(link)
    setActiveItem(null)
  }

  const getBasePathForKey = (key) => {
    const item = nav?.navItems?.find(item => item.key === key)
    if (item?.links?.[0]?.path) {
      // For insights, check if it's blogs or other
      if (key == "company") return "/contact"

      if (key === 'insights') {
        const blogsLink = item.links.find(link => link.label.toLowerCase().includes('blog'))
        return blogsLink?.path || item.links[0].path
      }

      return item.links[0].path.split('/').slice(0, 2).join('/') || '/'
    }

    switch (key) {
      case 'services':
        return '/services'
      case 'industries':
        return '/industries'
      case 'insights':
        return '/blogs'
      case 'company':
        return '/contact'
      default:
        return '/'
    }
  }

  const pathGroupMap = {
    industries: 'industries',
    services: 'services',
    blogs: 'insights',
    blog: 'insights',
    webinars: 'insights',
    about: 'company',
    careers: 'company',
    contact: 'company',
    'case-studies': 'insights'
  }

  const isPathActive = (basePath, item) => {
    if (!basePath) return false

    const path = location.pathname.split('/')[1] // first segment

    const mapped = pathGroupMap[path]

    // normal matching
    if (path === basePath.replace('/', '')) return true

    // mapped matching
    return mapped === item.key
  }

  // Get card data from global config for stats
  const cards = nav?.clientImages || []

  if (globalLoading || !nav) {
    return (
      <header className="text-white shadow-sm sticky top-0 z-50 bg-black">
        <div className="container-custom">
          <div className="flex justify-between items-center h-16">
            <div className="w-32 h-8 bg-black-200 rounded animate-pulse"></div>
            <div className="hidden md:flex space-x-8">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="w-20 h-6 bg-black-200 rounded animate-pulse"
                ></div>
              ))}
            </div>
            <div className="w-24 h-10 bg-black-200 rounded animate-pulse"></div>
          </div>
        </div>
      </header>
    )
  }

  const { logoUrl, buttonText, buttonLink, navItems } = nav
  const isSpecialLayout = activeItem !== null && navItems[activeItem]?.key === 'company'

  return (
    <nav className={`premium-navbar ${showNavbar ? `down ${hasScrolledPast100 ? "down-blur" : ""}` : isMobile && isMenuOpen ? "down" : ""}`}>
      {/* Logo */}
      <motion.div
        className="brand-logo"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        onClick={() => {
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          })
          navigate("/")
        }}
        style={{ cursor: "pointer" }}
      >
        {logoUrl ? (
          <img src={logoUrl} alt="Logo" className="h-8 w-auto object-contain" />
        ) : (
          <div className="w-8 h-8 bg-primary-600 rounded-lg" />
        )}
      </motion.div>

      {isMobile ? (
        <>
          <motion.button
            className="mobile-menu-button"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            onClick={() => setIsMenuOpen(true)}
            aria-label="Open menu"
            style={{ cursor: "pointer" }}
          >
            <IoMenuOutline fontSize={32} />
          </motion.button>

          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                className="mobile-menu-overlay"
                initial={{ opacity: 0, x: "100%" }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
              >
                <div className="mobile-menu-header">
                  <div
                    className="brand-logo"
                    onClick={() => {
                      navigate("/")
                      setIsMenuOpen(false)
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    {logoUrl ? (
                      <img src={logoUrl} alt="Logo" className="h-8 w-auto object-contain" />
                    ) : (
                      <div className="w-8 h-8 bg-primary-600 rounded-lg" />
                    )}
                  </div>
                  <button
                    className="mobile-close-button"
                    onClick={() => {
                      setIsMenuOpen(false)
                      setMobileDropdownOpen(null)
                    }}
                    aria-label="Close menu"
                    style={{ cursor: "pointer" }}
                  >
                    <IoClose color="white" fontSize={32} />
                  </button>
                </div>

                <div className="mobile-menu-content">
                  {navItems?.map((item) => {
                    const basePath = getBasePathForKey(item.key)
                    const isActive = isPathActive(basePath, item)

                    return (
                      <div key={item.key} className="mobile-menu-item">
                        {item.links && item.links.length > 0 ? (
                          <>
                            <button
                              className={`mobile-dropdown-button ${mobileDropdownOpen === item.key ? "open" : ""} ${isActive ? "active" : ""}`}
                              onClick={() => handleMobileItemClick(item.key)}
                              style={{ cursor: "pointer" }}
                            >
                              {item.title}
                              <motion.span
                                className="dropdown-arrow"
                                animate={{
                                  rotate: mobileDropdownOpen === item.key ? 180 : 0,
                                }}
                                transition={{ duration: 0.3 }}
                              >
                                <IoIosArrowDown />
                              </motion.span>
                            </button>

                            <AnimatePresence>
                              {mobileDropdownOpen === item.key && (
                                <motion.div
                                  className="mobile-dropdown-content"
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.3 }}
                                >
                                  {dropdownContent[item.key]?.cols?.flat().map((subItem, index) => (
                                    <div
                                      key={`mobile-${item.key}-${index}`}
                                      className="mobile-dropdown-item"
                                      onClick={() => handleMobileLinkClick(subItem.link)}
                                      style={{ cursor: "pointer" }}
                                    >
                                      <span className="item-icon">
                                        {subItem.icon}
                                      </span>
                                      <div className="item-text">
                                        <h4>{subItem.title}</h4>
                                        <p>{subItem.desc}</p>
                                      </div>
                                    </div>
                                  ))}

                                  {dropdownContent[item.key]?.leftCol?.map((subItem, index) => (
                                    <div
                                      key={`mobile-${item.key}-${index}`}
                                      className="mobile-dropdown-item"
                                      onClick={() => handleMobileLinkClick(subItem.link)}
                                      style={{ cursor: "pointer" }}
                                    >
                                      <span className="item-icon">
                                        {subItem.icon}
                                      </span>
                                      <div className="item-text">
                                        <h4>{subItem.title}</h4>
                                        <p>{subItem.desc}</p>
                                      </div>
                                    </div>
                                  ))}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </>
                        ) : (
                          <button
                            className={`mobile-menu-link ${isActive ? "active" : ""}`}
                            onClick={() => {
                              navigate(basePath)
                              setIsMenuOpen(false)
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            {item.title}
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>

                <div className="mobile-menu-footer">
                  {/* {buttonText && (
                    <motion.button
                      className="mobile-contact-button"
                      whileHover={{
                        boxShadow: "0 0 15px rgba(74, 165, 94, 0.5)",
                      }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        handleOpenContactModal();
                        setIsMenuOpen(false);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      {buttonText}
                    </motion.button>
                  )} */}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      ) : (
        <>
          <div className="nav-center" ref={navCenterRef}>
            {navItems?.map((item, index) => {
              const basePath = getBasePathForKey(item.key)
              const isActive = isPathActive(basePath, item)

              return (
                <motion.button
                  key={item.key}
                  ref={(el) => (itemsRef.current[index] = el)}
                  className={`nav-item ${activeItem === index ? "active" : ""} ${isActive ? "path-active" : ""}`}
                  onMouseEnter={() => handleItemHover(index)}
                  onClick={() => handleItemClick(index)}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{
                    opacity: 1,
                    y: 0
                  }}
                  transition={{
                    duration: 0.3,
                    delay: index * 0.1,
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <span style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    {item.title} {item.links && item.links.length > 0 && <IoIosArrowDown style={{ marginTop: "2px" }} fontSize={12} />}
                  </span>
                </motion.button>
              )
            })}
            <motion.div
              ref={underlineRef}
              className="nav-underline"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.5 }}
            />
          </div>

          {buttonText && (
            <motion.span
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/contact")}
              style={{ cursor: "pointer" }}
            >
              <StarBorder
                as="button"
                className="custom-class"
                color="var(--primary)"
                speed="6s"
              >
                {buttonText}
              </StarBorder>
            </motion.span>
          )}
          <AnimatePresence>
            {activeItem !== null && navItems[activeItem]?.links && navItems[activeItem].links.length > 0 && (
              <motion.div
                className="nav-dropdown-container"
                initial={{ opacity: 0, y: -20, height: 0 }}
                animate={{ opacity: 1, y: 0, height: "auto" }}
                exit={{ opacity: 0, y: -20, height: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                ref={dropdownRef}
                onMouseEnter={handleDropdownHover}
                onMouseLeave={handleDropdownLeave}
              >
                <div className="nav-dropdown">
                  {isSpecialLayout ? (
                    <div className="dropdown-content-row special-layout">
                      <div className="dropdown-col">
                        <div className="dropdown-title-row">
                          <h3>{navItems[activeItem].title}</h3>
                        </div>
                        {dropdownContent[navItems[activeItem].key].leftCol.map(
                          (item, index) => (
                            <div
                              className="dropdown-item"
                              key={`left-item-${index}`}
                              onClick={() => handleDesktopLinkClick(item.link)}
                              style={{ cursor: "pointer" }}
                            >
                              <span className="item-icon">
                                {item.icon}
                              </span>
                              <div className="item-text">
                                <h4>{item.title}</h4>
                                <p>{item.desc}</p>
                              </div>
                            </div>
                          )
                        )}
                      </div>

                      <div className="dropdown-col right-col">
                        <div className="right-col-header">
                          <div className="dropdown-title-row">
                            <h3>
                              {dropdownContent[navItems[activeItem].key].rightTitle}
                            </h3>
                            <button
                              className="view-all-button"
                              onClick={() => {
                                navigate("/blogs")
                                setActiveItem(null)
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              View All
                              <IoArrowForward style={{ marginLeft: "5px" }} fontSize={15} />
                            </button>
                          </div>
                        </div>

                        <div className="insights-list">
                          {blogsLoading ? (
                            // Loading skeleton for insights
                            [...Array(2)].map((_, index) => (
                              <div key={`loading-${index}`} className="insight-item">
                                <div className="insight-content">
                                  <div className="insight-image skeleton-bg bg-white" style={{ width: '150px', height: '85px' }} />
                                  <div className="insight-details">
                                    <div className="skeleton-bg bg-white" style={{ width: '100%', height: '20px', marginBottom: '8px' }} />
                                    <div className="tech-capsules">
                                      <span className="tech-capsule skeleton-bg bg-white" style={{ width: '60px', height: '24px' }} />
                                      <span className="tech-capsule skeleton-bg bg-white" style={{ width: '50px', height: '24px' }} />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : dropdownContent[navItems[activeItem].key].insights.map(
                            (insight, index) => (
                              <div
                                key={`insight-${insight.id || index}`}
                                className="insight-item"
                                onClick={() => handleDesktopLinkClick(insight.link)}
                                style={{ cursor: "pointer" }}
                              >
                                <div className="insight-content">
                                  <div className="insight-image">
                                    <LazyImage
                                      src={insight.image}
                                      alt={insight.title}
                                      className="insight-image"
                                    />
                                  </div>
                                  <div className="insight-details">
                                    <h4>{insight.title}</h4>
                                    <div className="tech-capsules">
                                      {insight.tech.map((tech, techIndex) => (
                                        <span
                                          key={`tech-${techIndex}`}
                                          className="tech-capsule"
                                        >
                                          {tech}
                                        </span>
                                      ))}
                                    </div>
                                    {insight.publishedAt && (
                                      <div className="insight-date">
                                        {formatDate(insight.publishedAt)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {index < dropdownContent[navItems[activeItem].key].insights.length - 1 && <div className="insight-divider" />}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="dropdown-title-row">
                        <h3>{navItems[activeItem].title}</h3>
                      </div>

                      <div className="dropdown-content-row">
                        {dropdownContent[navItems[activeItem].key].cols.map(
                          (col, colIndex) => (
                            <div
                              className="dropdown-col"
                              key={`col-${colIndex}`}
                            >
                              {col.map((item, itemIndex) => (
                                <div
                                  className="dropdown-item"
                                  key={`item-${itemIndex}`}
                                  onClick={() => handleDesktopLinkClick(item.link)}
                                  style={{ cursor: "pointer" }}
                                >
                                  <span className="item-icon">
                                    {item.icon}
                                  </span>
                                  <div className="item-text">
                                    <h4>{item.title}</h4>
                                    <p>{item.desc}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )
                        )}
                      </div>

                      {navItems[activeItem].key === "services" && (
                        <div className="dropdown-clients-row">
                          <div style={{ display: "flex", alignItems: "start", gap: "1rem", justifyContent: "center" }}>
                            <div className="clients-logos">
                              {cards.slice(0, 3).map((card, index) => (
                                <div
                                  className="client-logo"
                                  key={`client-${index}`}
                                  style={{ cursor: "pointer" }}
                                >
                                  <img
                                    src={card}
                                    alt={card || `Client ${index + 1}`}
                                    className="w-8 h-8"
                                  />
                                </div>
                              ))}
                            </div>
                            <span className="clients-count">
                              <h4>{nav?.clientTextOne || "5+"}</h4>
                              <p>{nav?.clientTextTwo || "Happy clients"}</p>
                            </span>
                          </div>
                          <button
                            className="view-all-button"
                            onClick={() => {
                              handleOpenContactModal();
                              setActiveItem(null)
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            Schedule a Free Consultation
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </nav>
  )
}